import { Input, InputNumber, Tooltip } from "antd"
import { LockOutlined, ThunderboltOutlined } from "@ant-design/icons"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { placementsEnum } from "../types/types"
import { COLUMNS_SIZE_MAP } from "../constants/table.constants"
import { IPlacementDataTable } from "../types/placement"
import SelectAudience from "../components/SelectAudience"
import FvVideoPreview from "../components/FvVideoPreview"
import { IPlacementDataForUpdate } from "../store/types/placements"
import { DEFAULT_DYNAMIC_ACOS_MIN_MARKET_PURCHASES } from "./usePlacementMetrics"
import { LadderFixedAcosBadge } from "../../../components/LadderFixedAcosBadge/LadderFixedAcosBadge"

interface IUsePlacementColumnsProps {
  handleLimitedCPCChange: (placement: IPlacementDataTable, value: number) => void
  handleTargetAcosChange: (placement: IPlacementDataTable, value: number) => void
  setPlacementsDataForUpdate: React.Dispatch<React.SetStateAction<IPlacementDataForUpdate[]>>
}

const EMPTY_VALUE = "-"

export function targetAcosModeHint(
  placement: IPlacementDataTable,
  minVolume: number
): string | null {
  const volume = placement.marketPurchases4w ?? "n/a"
  if (placement.targetAcosMode === "fixed-low-volume") {
    return `Fixed — market ${volume}/4wk < ${minVolume}`
  }
  if (placement.targetAcosMode === "dynamic") {
    return `Dynamic — market ${volume}/4wk`
  }
  return null
}

function TargetAcosModeIcon({
  placement,
  minVolume,
}: {
  placement: IPlacementDataTable
  minVolume: number
}) {
  const hint = targetAcosModeHint(placement, minVolume)
  if (!hint) return null
  const lowVolume = placement.targetAcosMode === "fixed-low-volume"
  return (
    <Tooltip title={hint}>
      {lowVolume ? (
        <LockOutlined aria-label={hint} style={{ color: "#faad14", marginLeft: 4 }} />
      ) : (
        <ThunderboltOutlined aria-label={hint} style={{ color: "#52c41a", marginLeft: 4 }} />
      )}
    </Tooltip>
  )
}

const usePlacementColumns = (params: IUsePlacementColumnsProps) => {
  const { handleLimitedCPCChange, handleTargetAcosChange, setPlacementsDataForUpdate } = params
  const minVolume = useTypedSelector(
    (select) =>
      select.sponsoredProductsSKC.campaignData.dynamicAcosMinMarketPurchases ??
      DEFAULT_DYNAMIC_ACOS_MIN_MARKET_PURCHASES
  )
  const ladderPolicy = useTypedSelector(
    (select) => select.sponsoredProductsSKC.campaignData.ladderPolicy
  )

  return [
    {
      width: COLUMNS_SIZE_MAP["1"] + 80,
    },
    {
      dataIndex: "targetAcos",
      render: (targetAcos: number, placement: IPlacementDataTable) => (
        <>
          <Input
            value={targetAcos}
            type="text"
            className="cal"
            onChange={(e) => handleTargetAcosChange(placement, Number(e.target.value))}
          />
          <TargetAcosModeIcon placement={placement} minVolume={minVolume} />
          <LadderFixedAcosBadge
            active={placement.targetAcosMode === "fixed-ladder"}
            ladderPolicy={ladderPolicy}
          />
        </>
      ),
      width: COLUMNS_SIZE_MAP["2"],
    },
    {
      render: (row: IPlacementDataTable) => {
        if (row.placement === placementsEnum.AUDIENCE) {
          return (
            <SelectAudience
              audienceId={row.audienceId}
              campaignId={row.campaignId}
              setPlacementsDataForUpdate={setPlacementsDataForUpdate}
            />
          )
        }
        if (row.placement === placementsEnum.FEATURED_VIDEOS) {
          return <FvVideoPreview placement={row} />
        }
        return row.placement
      },
      width: COLUMNS_SIZE_MAP["3"],
    },
    {
      width: COLUMNS_SIZE_MAP["4"],
    },
    {
      width: COLUMNS_SIZE_MAP["5"],
    },
    {
      dataIndex: "placementModifier",
      render: (e) => `${e ? e : 0}%`,
      width: COLUMNS_SIZE_MAP["6"],
    },
    {
      dataIndex: "targetCPCAmazon",
      render: (e) => `${e || 0}$`,
      width: COLUMNS_SIZE_MAP["7"],
    },
    {
      dataIndex: "targetCPC",
      render: (targetCPC) => `${targetCPC?.toFixed(2) || 0}$`,
      width: COLUMNS_SIZE_MAP["8"],
    },
    {
      dataIndex: "bestCPC",
      render: (bestCPC: number | null) =>
        bestCPC === null ? EMPTY_VALUE : `${Number(bestCPC || 0).toFixed(2)}$`,
      width: COLUMNS_SIZE_MAP["9"],
    },
    {
      dataIndex: "limitedCPC",
      render: (limitedCPC: number, placement: IPlacementDataTable) => (
        <InputNumber
          value={limitedCPC?.toFixed(2) || 0}
          step="0.01"
          onChange={(value) => handleLimitedCPCChange(placement, Number(value))}
          stringMode
        />
      ),
      width: COLUMNS_SIZE_MAP["10"],
    },
    {
      title: "New Bid Adjustment",
      dataIndex: "newBidAdjustment",
      render: (newBidAdjustment: number) => (newBidAdjustment ?? 0).toFixed(0) + "%",
      width: COLUMNS_SIZE_MAP["11"],
    },
    {
      dataIndex: "impressions",
      render: (e) => e,
      width: COLUMNS_SIZE_MAP["12"],
    },
    {
      dataIndex: "clicks",
      render: (e) => e,
      width: COLUMNS_SIZE_MAP["13"],
    },
    {
      dataIndex: "ctr",
      render: (e) => `${e}%`,
      width: COLUMNS_SIZE_MAP["14"],
    },
    {
      dataIndex: "spend",
      render: (e) => `${e}$`,
      width: COLUMNS_SIZE_MAP["15"],
    },
    {
      dataIndex: "sales",
      render: (e) => `${e}$`,
      width: COLUMNS_SIZE_MAP["16"],
    },
    {
      dataIndex: "orders",
      render: (e) => e,
      width: COLUMNS_SIZE_MAP["17"],
    },
    {
      dataIndex: "units",
      render: (e) => e,
      width: COLUMNS_SIZE_MAP["18"],
    },
    {
      dataIndex: "cvr",
      render: (e) => `${e}%`,
      width: COLUMNS_SIZE_MAP["19"],
    },
    {
      render: () => EMPTY_VALUE,
      width: COLUMNS_SIZE_MAP["20"],
    },
    {
      render: () => EMPTY_VALUE,
      width: COLUMNS_SIZE_MAP["21"],
    },
    {
      dataIndex: "acos",
      render: (e) => `${e}%`,
      width: COLUMNS_SIZE_MAP["22"],
    },
    {
      dataIndex: "cpc",
      render: (e) => `${e}$`,
      width: COLUMNS_SIZE_MAP["23"],
    },
    {
      dataIndex: "roas",
      render: (e) => e,
      width: COLUMNS_SIZE_MAP["24"],
    },
  ]
}

export default usePlacementColumns
